import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo } from 'react'
import { useFormik } from 'formik'

import EditForm from '../../../../../../../features/components/Forms/EditForm'
import PositionFields from '../../../../../reusableFormFields/PositionFields'

import { useEditSectionFormSubmit } from '../../../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { usePurifiedFormik } from '../../../../../../../hooks/formHooks/usePurifiedFormik'

import {
  dv360BulkTargetingWasUpdatedSelector,
  dv360TargetingSelector,
  updateDv360BulkTargetingErrorSelector,
  updateDv360BulkTargetingIsLoadingSelector
} from '../../../../../../../modules/selectors/dv360Constants'
import { selectedDV360AdAccountSelector } from '../../../../../../../modules/selectors/adAccounts'
import {
  clearUpdateDv360BulkTargeting,
  updateDv360BulkTargeting
} from '../../../../../../../modules/actions/dv360Constants'

import { targetingTypes } from '../../../../LineItemDv360Create/fields'
import {
  CONTENT_INSTREAM_POSITIONS,
  CONTENT_OUTSTREAM_POSITIONS,
  ON_SCREEN_POSITIONS
} from '../../../../../UploadDV360AdCreative/UploadDV360AdCreativeForm/LineItemForm/fields'
import { HISTORY_META_EDIT_SECTION, TYPE_LINE_ITEM } from '../../../../../../../constants/changeHistory'
import { getInitialValues } from '../fields'

function PositionsSectionForm({ editItemData: item, title, ...formProps }) {
  const dispatch = useDispatch()

  const targeting = useSelector(dv360TargetingSelector)
  const dv360BulkTargetingWasUpdated = useSelector(dv360BulkTargetingWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const initialValues = useMemo(() => {
    return getInitialValues(targeting)
  }, [targeting])

  const handleSubmit = useCallback(
    values => {
      const onScreenPositionsTargeting = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_ON_SCREEN_POSITION,
          assigned_targeting_options: values[ON_SCREEN_POSITIONS].map(item => ({
            on_screen_position: item.value,
            targeting_option_id: item.targetingOptionId
          }))
        }
      ]

      const contentInstreamPositionsTargeting = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_CONTENT_INSTREAM_POSITION,
          assigned_targeting_options: values[CONTENT_INSTREAM_POSITIONS].map(item => ({
            content_instream_position: item.value,
            targeting_option_id: item.targetingOptionId
          }))
        }
      ]

      const contentOutstreamPositionsTargeting = [
        {
          targeting_type: targetingTypes.TARGETING_TYPE_CONTENT_OUTSTREAM_POSITION,
          assigned_targeting_options: values[CONTENT_OUTSTREAM_POSITIONS].map(item => ({
            content_outstream_position: item.value,
            targeting_option_id: item.targetingOptionId
          }))
        }
      ]

      const updateData = {
        account: selectedAdAccountId,
        line_item_id: item.id,
        requests: [
          ...onScreenPositionsTargeting,
          ...contentInstreamPositionsTargeting,
          ...contentOutstreamPositionsTargeting
        ],
        meta: {
          [HISTORY_META_EDIT_SECTION]: title,
          override: {
            item_id: item.id,
            item_type: TYPE_LINE_ITEM,
            action: 'update'
          }
        }
      }

      dispatch(updateDv360BulkTargeting(updateData, true))
    },
    [dispatch, item.id, selectedAdAccountId, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateItem = useCallback(() => {
    dispatch(clearUpdateDv360BulkTargeting())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={manualSuccessSubmit || dv360BulkTargetingWasUpdated}
      clearEditItem={handleClearUpdateItem}
      errorSelector={updateDv360BulkTargetingErrorSelector}
      isLoadingSelector={updateDv360BulkTargetingIsLoadingSelector}
      {...formProps}
    >
      <PositionFields formik={formik} />
    </EditForm>
  )
}

export default PositionsSectionForm
